// OAuth utility functions
import { getBasePath } from './pathUtils';

export const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
};

export const base64URLEncode = (array: Uint8Array): string => {
  return btoa(String.fromCharCode.apply(null, Array.from(array)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

export const generateCodeChallenge = async (codeVerifier: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64URLEncode(new Uint8Array(digest));
};

// OAuth configuration objects
export interface OAuthConfig {
  clientId?: string;
  clientKey?: string;
  redirectUri: string;
  scope: string;
  authUrl: string;
  /** Facebook Login for Business configuration id; set, it replaces `scope`. */
  configId?: string;
}

export const TWITTER_CONFIG: OAuthConfig = {
  clientId: process.env.REACT_APP_TWITTER_CLIENT_ID || 'default_client_id',
  redirectUri:
    process.env.REACT_APP_TWITTER_REDIRECT_URI ||
    `${window.location.origin}${getBasePath()}/auth/twitter/callback`,
  scope: 'tweet.read tweet.write media.write users.read offline.access',
  authUrl: 'https://x.com/i/oauth2/authorize',
};

// One Meta sign-in: the Pages the person manages (pages_*) and the Instagram
// professional accounts linked to them (instagram_basic, instagram_content_publish).
// The API server lists both from /me/accounts and the person picks which to
// connect. REACT_APP_FACEBOOK_SCOPE overrides the list; REACT_APP_FACEBOOK_CONFIG_ID
// switches the dialog to a Facebook Login for Business configuration instead.
export const FACEBOOK_CONFIG: OAuthConfig = {
  clientId: process.env.REACT_APP_FACEBOOK_APP_ID || 'default_app_id',
  redirectUri:
    process.env.REACT_APP_FACEBOOK_REDIRECT_URI ||
    `${window.location.origin}${getBasePath()}/auth/facebook/callback`,
  scope:
    process.env.REACT_APP_FACEBOOK_SCOPE ||
    'pages_show_list,pages_read_engagement,pages_manage_metadata,pages_manage_posts,pages_read_user_content,pages_manage_engagement,business_management,read_insights,instagram_basic,instagram_content_publish',
  authUrl: 'https://www.facebook.com/v23.0/dialog/oauth',
  configId: process.env.REACT_APP_FACEBOOK_CONFIG_ID || undefined,
};

export const THREADS_CONFIG: OAuthConfig = {
  clientId: process.env.REACT_APP_THREADS_APP_ID || 'default_app_id',
  redirectUri:
    process.env.REACT_APP_THREADS_REDIRECT_URI ||
    `${window.location.origin}${getBasePath()}/auth/threads/callback`,
  scope:
    'threads_basic,threads_content_publish,threads_manage_insights,threads_keyword_search,threads_location_tagging,threads_manage_mentions,threads_profile_discovery,threads_read_replies,threads_delete,threads_manage_replies',
  authUrl: 'https://threads.net/oauth/authorize',
};

export const INSTAGRAM_CONFIG: OAuthConfig = {
  clientId: process.env.REACT_APP_INSTAGRAM_APP_ID || 'default_app_id',
  redirectUri:
    process.env.REACT_APP_INSTAGRAM_REDIRECT_URI ||
    `${window.location.origin}${getBasePath()}/auth/instagram/callback`,
  scope:
    'instagram_business_basic,instagram_business_content_publish,instagram_business_manage_insights,instagram_business_manage_messages,instagram_business_manage_comments',
  authUrl: 'https://api.instagram.com/oauth/authorize',
};

export const YOUTUBE_CONFIG: OAuthConfig = {
  clientId: process.env.REACT_APP_YOUTUBE_CLIENT_ID || 'default_client_id',
  redirectUri:
    process.env.REACT_APP_YOUTUBE_REDIRECT_URI ||
    `${window.location.origin}${getBasePath()}/auth/youtube/callback`,
  scope:
    'https://www.googleapis.com/auth/youtube.upload https://www.googleapis.com/auth/youtube https://www.googleapis.com/auth/youtube.force-ssl',
  authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
};

export const TIKTOK_CONFIG: OAuthConfig = {
  clientKey: process.env.REACT_APP_TIKTOK_CLIENT_KEY || 'default_client_key',
  redirectUri:
    process.env.REACT_APP_TIKTOK_REDIRECT_URI ||
    `${window.location.origin}${getBasePath()}/auth/tiktok/callback`,
  scope: 'user.info.basic,user.info.profile,video.upload,video.publish',
  authUrl: 'https://www.tiktok.com/v2/auth/authorize',
};

export const SNAPCHAT_CONFIG: OAuthConfig = {
  clientId: process.env.REACT_APP_SNAPCHAT_CLIENT_ID || 'default_client_id',
  redirectUri:
    process.env.REACT_APP_SNAPCHAT_REDIRECT_URI ||
    `${window.location.origin}${getBasePath()}/auth/snapchat/callback`,
  scope: 'user.external_id,user.display_name,user.bitmoji.avatar,user.bitmoji.avatar.id',
  authUrl: 'https://accounts.snapchat.com/login/oauth2/authorize',
};
